import type { TFunction } from 'i18next';
import { isInternalReportError } from 'util/reportErrorMessage';

type AxiosResponseLike = {
    status?: number;
    data?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const normalizeBackendMsg = (msg: unknown): string | undefined => {
    if (typeof msg === 'string') return msg;
    if (isRecord(msg)) {
        const entries = Object.entries(msg);
        if (entries.length > 0) {
            return entries.map(([k, v]) => `${k}: ${String(v)}`).join(', ');
        }
    }
    if (msg == null) return undefined;
    return String(msg);
};

const extractBackendMsgFromData = (data: unknown): string | undefined => {
    if (!isRecord(data)) return undefined;
    return normalizeBackendMsg(data.msg);
};

const extractRequestIdFromData = (data: unknown): string | undefined => {
    if (!isRecord(data)) return undefined;
    const topLevel = data.request_id;
    if (typeof topLevel === 'string' && topLevel.length > 0) {
        return topLevel;
    }
    const envelope = data.error;
    if (isRecord(envelope) && typeof envelope.request_id === 'string') {
        return envelope.request_id;
    }
    return undefined;
};

const API_TECHNICAL_PATTERNS = [
    /could not prepare statement/i,
    /could not execute statement/i,
    /could not process query/i,
    /could not get rows affected/i,
    /no rows affected/i,
    /sql:/i,
    /\bdeadlock\b/i,
    /connection refused/i,
    /dial tcp/i,
    /panic:/i,
];

export const isTechnicalApiErrorMessage = (message: string): boolean => {
    const trimmed = message.trim();
    if (!trimmed) return true;
    if (isInternalReportError(trimmed)) return true;
    return API_TECHNICAL_PATTERNS.some((pattern) => pattern.test(trimmed));
};

export type ParsedApiError = {
    statusCode: number;
    backendMsg?: string;
    requestId?: string;
};

export const parseApiErrorResponse = (
    res: AxiosResponseLike | undefined | null
): ParsedApiError => {
    if (!res || typeof res.status !== 'number') {
        return { statusCode: 0 };
    }

    return {
        statusCode: res.status,
        backendMsg: extractBackendMsgFromData(res.data),
        requestId: extractRequestIdFromData(res.data),
    };
};

const translateOrFallback = (key: string, t?: TFunction): string => {
    if (t) return t(key);
    const fallbacks: Record<string, string> = {
        'errors.internal_server_error':
            'A system error occurred. Please try again shortly.',
        'errors.unauthorized':
            'Your session has expired. Please sign in again to continue.',
        'errors.forbidden':
            'You do not have permission to perform this action.',
        'errors.not_found': 'The requested resource was not found.',
        'errors.conflict':
            'A data conflict occurred. Refresh the page and try again.',
        'errors.unprocessable':
            'Some fields contain invalid values. Please correct them and try again.',
        'errors.bad_request':
            'The request could not be processed. Please verify your information and try again.',
        'errors.network':
            'The connection to the server could not be established. Check your internet connection and try again.',
        'errors.unknown_error':
            'An unexpected error occurred. Please try again.',
    };
    return fallbacks[key] ?? key;
};

export type UserFacingApiError = {
    message: string;
    requestId?: string;
};

export const resolveUserFacingApiError = (
    res: AxiosResponseLike | undefined | null,
    t?: TFunction
): UserFacingApiError => {
    const parsed = parseApiErrorResponse(res);
    const { statusCode, backendMsg, requestId } = parsed;

    if (statusCode === 0) {
        return {
            message: translateOrFallback('errors.network', t),
        };
    }

    if (statusCode >= 500) {
        return {
            message: translateOrFallback('errors.internal_server_error', t),
            requestId,
        };
    }

    if (statusCode === 401) {
        return { message: translateOrFallback('errors.unauthorized', t) };
    }

    if (statusCode === 403) {
        return { message: translateOrFallback('errors.forbidden', t) };
    }

    if (statusCode === 422) {
        if (backendMsg && !isTechnicalApiErrorMessage(backendMsg)) {
            return { message: backendMsg };
        }
        return {
            message: translateOrFallback('errors.unprocessable', t),
        };
    }

    if (statusCode === 404) {
        if (backendMsg && !isTechnicalApiErrorMessage(backendMsg)) {
            return { message: backendMsg };
        }
        return { message: translateOrFallback('errors.not_found', t) };
    }

    if (statusCode === 409) {
        if (backendMsg && !isTechnicalApiErrorMessage(backendMsg)) {
            return { message: backendMsg };
        }
        return { message: translateOrFallback('errors.conflict', t) };
    }

    if (statusCode === 400) {
        if (backendMsg && !isTechnicalApiErrorMessage(backendMsg)) {
            return { message: backendMsg };
        }
        return { message: translateOrFallback('errors.bad_request', t) };
    }

    if (backendMsg && !isTechnicalApiErrorMessage(backendMsg)) {
        return { message: backendMsg };
    }

    return { message: translateOrFallback('errors.unknown_error', t) };
};

export const legacyApiErrorFromResponse = (
    res: AxiosResponseLike | undefined | null
): {
    error: string;
    statusCode: number;
    requestId?: string;
} => {
    const parsed = parseApiErrorResponse(res);
    const userFacing = resolveUserFacingApiError(res);
    return {
        error: userFacing.message,
        statusCode: parsed.statusCode || 0,
        requestId: userFacing.requestId,
    };
};

const isSuccessStatus = (status: number): boolean =>
    status >= 200 && status < 300;

export const apiErrorIfFailed = (
    res: AxiosResponseLike | undefined | null
):
    | { error: null; statusCode: number }
    | { error: string; statusCode: number; requestId?: string } => {
    if (!res || typeof res.status !== 'number') {
        const err = resolveUserFacingApiError(res);
        return { error: err.message, statusCode: 0, requestId: err.requestId };
    }
    if (isSuccessStatus(res.status)) {
        return { error: null, statusCode: res.status };
    }
    const err = legacyApiErrorFromResponse(res);
    return {
        error: err.error,
        statusCode: err.statusCode,
        requestId: err.requestId,
    };
};
